import { AxiosResponse } from 'axios';

import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import {
  ICreateShiftData,
  ICreateShiftResponse,
  IDeleteShiftsFilters,
  IGetShiftsFilters,
  IGetShiftsResponse,
  IGetSingleShiftResponse,
  IUpdateShiftData,
  IDeleteShiftsResponse,
  IUpdateShiftResponse,
  IActivateShiftResponse,
  IDeactivateShiftResponse,
  IGetShiftsSimpleListFilters,
} from 'models/http/shift';
import { HTTP } from './core.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import DataUploadBaseHttp from './utils/data-upload.http';
import { IGetSimpleListResponse } from 'models/http/common';

class ShiftsHttp extends DataUploadBaseHttp implements HTTP<IDeleteShiftsFilters> {
  protected readonly route: DataUploadModels = ModelsNames.Shifts;

  public async getSimple(filters?: IGetShiftsSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    const queryParams = prepareQPFromFilters({ isActive: filters?.isActive });

    return serverAxios
      .get(`/${this.route}/simple?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async get(filters?: IGetShiftsFilters, abortController?: AbortController): Promise<IGetShiftsResponse['records']> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetShiftsResponse>) => {
        return response.data.records;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleShiftResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleShiftResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateShiftData): Promise<string> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateShiftResponse>) => {
        return response.data.id;
      });
  }

  public async update(id: number, data: IUpdateShiftData): Promise<IUpdateShiftResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateShiftResponse>) => {
        return response.data;
      });
  }

  public async activateShift(id: number): Promise<IActivateShiftResponse> {
    return serverAxios
      .put(
        `/${this.route}/${id}/activate`,
        {},
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IActivateShiftResponse>) => response.data);
  }

  public async deactivateShift(id: number): Promise<IDeactivateShiftResponse> {
    return serverAxios
      .put(
        `/${this.route}/${id}/deactivate`,
        {},
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IDeactivateShiftResponse>) => response.data);
  }

  public async delete(ids: number[]): Promise<number>;
  public async delete(filters: IDeleteShiftsFilters): Promise<number>;
  public async delete(filters: number[] | IDeleteShiftsFilters): Promise<number> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IDeleteShiftsResponse>) => {
        return response.data.deleted;
      });
  }
}

export default ShiftsHttp;
